// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// ASP #24 — the manual trigger lives on Hold now (it was on Renewals). An event with no structured
// signal — a new diagnosis, an infusion reaction, a hospitalisation — puts the therapy ON HOLD with
// the pharmacist's reason, through the same audited door as "Send to Screening & Hold" (source manual).

import { useState } from 'react'
import EntityPicker, { type PickedEntity } from './EntityPicker'
import SearchableSelect from '../../components/common/SearchableSelect'
import { friendlyError } from '../../api/errors'
import { useOpenTherapies, usePlaceManualHold } from '../../hooks/queries/carerxClinical'

const lbl = { display: 'block', fontSize: 11.5, fontWeight: 600, color: '#41566a', marginBottom: 3 } as const

export default function ManualHoldPanel({ onPlaced, onCancel }: { onPlaced: (msg: string) => void; onCancel: () => void }) {
  const [patient, setPatient] = useState<PickedEntity | null>(null)
  const [therapyId, setTherapyId] = useState('')
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const therapies = useOpenTherapies(patient?.id ?? null)
  const place = usePlaceManualHold()
  const list = therapies.data ?? []
  // One open therapy needs no choice; several must be picked — guessing is the one wrong move.
  const chosen = therapyId || (list.length === 1 ? list[0].id : '')
  const ready = !!chosen && reason.trim().length > 0 && !place.isPending
  return (
    <div style={{ border: '1px solid #dde5ec', borderRadius: 10, padding: 14, marginBottom: 12 }} role="region" aria-label="Manual hold">
      <div style={{ fontSize: 13, fontWeight: 660, marginBottom: 10 }}>Put a therapy on hold</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, alignItems: 'end' }}>
        <div><span style={lbl}>Patient *</span><EntityPicker kind="patient" value={patient} onPick={p => { setPatient(p); setTherapyId('') }} /></div>
        <div><span style={lbl}>Therapy *</span>
          {!patient ? <span style={{ fontSize: 12, color: '#8496a5' }}>Pick a patient first</span>
            : therapies.isLoading ? <span style={{ fontSize: 12, color: '#8496a5' }}>Loading…</span>
            : list.length === 0 ? <span style={{ fontSize: 12, color: '#b45309' }}>No open therapy for this patient</span>
            : <SearchableSelect ariaLabel="Therapy" value={chosen} onChange={setTherapyId} placeholder="Choose the therapy…"
                options={list.map(t => ({ value: t.id, label: `${t.drug_name ?? 'Drug not set'} · Rx ${t.rx_number ?? '—'}` }))} />}
        </div>
        <div><span style={lbl}>Reason *</span>
          <input aria-label="Reason for the hold" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="New diagnosis / infusion reaction / hospitalization"
            style={{ width: '100%', fontSize: 12.5, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', boxSizing: 'border-box' }} /></div>
      </div>
      {err && <div role="alert" style={{ fontSize: 12, color: '#b23a48', marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button disabled={!ready} onClick={() => {
          setErr(null)
          place.mutate({ therapyId: chosen, reason: reason.trim() }, {
            onSuccess: () => onPlaced('Therapy put on hold.'),
            onError: e => setErr(friendlyError(e)),
          })
        }} style={{ fontSize: 12.5, fontWeight: 600, padding: '6px 14px', borderRadius: 7, border: 'none', background: '#185FA5', color: '#fff', cursor: ready ? 'pointer' : 'default', opacity: ready ? 1 : .55 }}>
          Put on hold
        </button>
        <button onClick={onCancel} style={{ fontSize: 12.5, padding: '6px 12px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  )
}
